import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { Checkbox, FormControlLabel } from '@mui/material';

// Services
import { searchEntities } from '../../../services/search.service';
import AuthService from '../../../services/auth.service';

// Utils
import { Storage } from '../../../utils/Storage';
import { makeSearchResultColumn } from '../../../utils/columns/makeSearchResultColumn';

// Types
import { SearchResult } from '../../../types/SearchResult';
import { PaginatedResult } from '../../../types/PaginatedResult';
import { FilterableTypes } from '../../../types/FilterableTypes';
import { SearchBarFilterTypes } from '../../../constants/Constants';

// Components
import { Table, useStandardTable as useTable } from '../../common/table';
import { SearchEntitiesRequest } from '../../../requestModels/searchEntitiesRequest';

const Search = () => {
  const params = useParams();
  const isExternalUser = AuthService.getStoredUserProfile()?.isInternal === false;
  const [paramFilter, setParamFilter] = useState<SearchEntitiesRequest>({
    wildcard: params.wildcard!,
    searchDevices: isExternalUser,
    searchEquipment: false,
    searchFacilities: false,
    searchReaders: false,
  });

  // below are the booleans indicating what to search. If all are false, everything has to be searched
  const [shouldSearchDevices, setShouldSearchDevices] = useState<boolean>(isExternalUser);
  const [shouldSearchEquipment, setShouldSearchEquipment] = useState<boolean>(false);
  const [shouldSearchFacilities, setShouldSearchFacilities] = useState<boolean>(false);
  const [shouldSearchReaders, setShouldSearchReaders] = useState<boolean>(false);
  const [resultCount, setResultCount] = useState<number | null>(null);

  useEffect(() => {
    setParamFilter((prev) => {
      const next = {
        ...prev,
        searchDevices: isExternalUser || shouldSearchDevices,
        searchEquipment: isExternalUser ? false : shouldSearchEquipment,
        searchFacilities: isExternalUser ? false : shouldSearchFacilities,
        searchReaders: isExternalUser ? false : shouldSearchReaders,
      };
      return _.isEqual(prev, next) ? prev : next; // bail out if unchanged
    });
  }, [isExternalUser, shouldSearchDevices, shouldSearchEquipment, shouldSearchFacilities, shouldSearchReaders]);
  const navigate = useNavigate();

  const navigateToEntity = (entity: SearchResult) => {
    switch (entity.type) {
      case 'Device':
        navigate(isExternalUser ? `/visits/${entity.data.deviceId}` : `/devices/${entity.data.deviceId}`);
        break;
      case 'Equipment':
        if (isExternalUser) {
          navigate(`/visits/${entity.description}?bySerial=true`);
        } else {
          navigate(`/equipment/${entity.id}`);
        }
        break;
      case 'Facility':
        Storage.setSearchBarFiltering(SearchBarFilterTypes.FACILITY_TMP, {
          isMoreFilter: false,
          filters: [{ id: entity.id, filterType: FilterableTypes.Equals }],
        });
        navigate(`/facilities`);
        break;
      case 'Reader':
        navigate(`/readers/${entity.data.imei}`);
        break;
      case 'Station':
        navigate(`/station/${entity.id}`);
        break;
    }
  };

  const entitiesSearched = (results: PaginatedResult<SearchResult>) => {
    setResultCount(results?.count ?? 0);

    if (
      results &&
      results.skipped == 0 &&
      results.count == 1 &&
      (!isExternalUser || ['Device', 'Equipment'].includes(results.results[0].type)) &&
      (results.results[0].description.toLowerCase() === params.wildcard?.toLowerCase() ||
        (results.results[0].type === 'Reader' && results.results[0].data.imei.toLowerCase() === params.wildcard?.toLowerCase()))
    ) {
      navigateToEntity(results.results[0]);
    }
  };

  const columns = useMemo(() => makeSearchResultColumn(), []);
  const table = useTable<any>({
    columns,
    accessKey: 'id',
    fetchAsync: async (params) => {
      try {
        return await searchEntities(params as SearchEntitiesRequest);
      } catch (err) {
        setResultCount(0); // treat a failed search the same as "not found" for external users
        throw err; // let useStandardTable's own catch still run notifyMessage/loading cleanup
      }
    },
    onFetchComplete: entitiesSearched,
    params: paramFilter,
    serverSide: true,
    onRowClick: navigateToEntity,
  });

  useEffect(() => {
    setResultCount(null);
    setParamFilter({ ...paramFilter, wildcard: params.wildcard! });
  }, [params.wildcard]);

  const showNotPairedMessage = isExternalUser && resultCount === 0;

  return (
    <>
      {showNotPairedMessage ? (
        <h4>This serial number is not paired with any device yet. Please try again later after the device is paired.</h4>
      ) : (
        <h2>Search results for &quot;{params.wildcard}&quot;</h2>
      )}

      {!isExternalUser && (
        <>
          <FormControlLabel label='Devices' control={<Checkbox size='medium' onChange={(event) => setShouldSearchDevices(event.target.checked)} />} />
          <FormControlLabel
            label='Equipment'
            control={<Checkbox size='medium' onChange={(event) => setShouldSearchEquipment(event.target.checked)} />}
          />
          <FormControlLabel
            label='Facilities'
            control={<Checkbox size='medium' onChange={(event) => setShouldSearchFacilities(event.target.checked)} />}
          />
          <FormControlLabel label='Readers' control={<Checkbox size='medium' onChange={(event) => setShouldSearchReaders(event.target.checked)} />} />
        </>
      )}

      {!showNotPairedMessage && (
        <Table {...table} showSearchBar={false} showColumnToggler={false} showAction={true} getCoreRowModel={getCoreRowModel()} />
      )}
    </>
  );
};

export default Search;
